/**
 * ShotRemindersCard — patient-facing scheduler for weekly injection reminders.
 * Free tier of the Companion App (no subscription required). Renders inside
 * the Body tab. Uses /api/wellness/shot-reminders CRUD.
 *
 * Weekday: 0=Mon...6=Sun (matches backend's Python `date.weekday()`).
 * time_hhmm: 24h HH:MM (native <input type="time">).
 * tz_offset_minutes: computed via `-new Date().getTimezoneOffset()` so the
 *   backend can convert current UTC to the patient's local wall clock for
 *   the "is it due right now?" check.
 */
import { useEffect, useState, useCallback } from "react";
import API from "@/lib/api";
import { toast } from "sonner";
import { Bell, BellRing, Plus, Trash2, Mail, MessageSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export default function ShotRemindersCard() {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [weekday, setWeekday] = useState(0);
  const [time, setTime] = useState("09:00");
  const [channel, setChannel] = useState("email");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/wellness/shot-reminders");
      setReminders(data.reminders || []);
    } catch {
      setReminders([]);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!time) { toast.error("Pick a time"); return; }
    setSaving(true);
    try {
      const tz_offset_minutes = -new Date().getTimezoneOffset();
      await API.post("/wellness/shot-reminders", {
        weekday, time_hhmm: time, channel, tz_offset_minutes,
        label: label.trim() || undefined,
      });
      toast.success("Reminder set");
      setShowForm(false);
      setLabel("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save reminder");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (r, next) => {
    try {
      await API.put(`/wellness/shot-reminders/${r.id}`, { active: next });
      setReminders((prev) => prev.map((x) => x.id === r.id ? { ...x, active: next } : x));
    } catch {
      toast.error("Couldn't update reminder");
    }
  };

  const remove = async (r) => {
    if (!window.confirm("Delete this reminder?")) return;
    try {
      await API.delete(`/wellness/shot-reminders/${r.id}`);
      setReminders((prev) => prev.filter((x) => x.id !== r.id));
    } catch {
      toast.error("Couldn't delete");
    }
  };

  const fmtTime = (hhmm) => {
    const [h, m] = hhmm.split(":").map(Number);
    const period = h >= 12 ? "PM" : "AM";
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${String(m).padStart(2, "0")} ${period}`;
  };

  return (
    <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4" data-testid="companion-shot-reminders">
      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex items-center gap-2">
          <BellRing className="w-4 h-4 text-purple-600" />
          <h3 className="text-sm font-bold text-slate-900 dark:text-white">Shot reminders</h3>
          <span className="text-[9px] uppercase tracking-wider font-bold text-emerald-700 bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300 px-1.5 py-0.5 rounded-full">Free</span>
        </div>
        {!showForm && (
          <Button
            variant="outline"
            className="rounded-full h-7 text-[11px] px-3"
            onClick={() => setShowForm(true)}
            data-testid="companion-shot-reminder-add-btn"
          >
            <Plus className="w-3 h-3 mr-1" /> Add
          </Button>
        )}
      </div>

      {loading && <p className="text-xs text-slate-400">Loading…</p>}

      {!loading && reminders.length === 0 && !showForm && (
        <p className="text-[11px] text-slate-500 leading-relaxed">
          Never miss your weekly shot. Add a reminder and we&apos;ll email or text you at your chosen day + time.
        </p>
      )}

      {!loading && reminders.length > 0 && (
        <ul className="space-y-2">
          {reminders.map((r) => (
            <li
              key={r.id}
              className="flex items-center gap-2 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 px-3 py-2"
              data-testid={`companion-shot-reminder-${r.id}`}
            >
              <Bell className={`w-3.5 h-3.5 ${r.active ? "text-purple-600" : "text-slate-400"}`} />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-bold text-slate-900 dark:text-white truncate">
                  {r.label || "Weekly shot"} · {WEEKDAY_LABELS[r.weekday]} {fmtTime(r.time_hhmm)}
                </p>
                <p className="text-[10px] text-slate-500 inline-flex items-center gap-1">
                  {r.channel === "email" && <><Mail className="w-2.5 h-2.5" /> email</>}
                  {r.channel === "sms" && <><MessageSquare className="w-2.5 h-2.5" /> SMS</>}
                  {r.channel === "both" && <><Mail className="w-2.5 h-2.5" /> email + <MessageSquare className="w-2.5 h-2.5" /> SMS</>}
                </p>
              </div>
              <Switch
                checked={!!r.active}
                onCheckedChange={(v) => toggleActive(r, v)}
                data-testid={`companion-shot-reminder-toggle-${r.id}`}
              />
              <button
                onClick={() => remove(r)}
                className="p-1 hover:bg-rose-50 rounded"
                data-testid={`companion-shot-reminder-delete-${r.id}`}
              >
                <Trash2 className="w-3.5 h-3.5 text-rose-500" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {showForm && (
        <div className="mt-3 space-y-3 border-t border-slate-100 dark:border-slate-800 pt-3">
          <div>
            <Label className="text-[11px]">Label (optional)</Label>
            <Input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Sema shot"
              maxLength={60}
              className="mt-1 rounded-xl text-sm"
              data-testid="companion-shot-reminder-label-input"
            />
          </div>
          <div>
            <Label className="text-[11px]">Day of week</Label>
            <div className="grid grid-cols-7 gap-1 mt-1">
              {WEEKDAY_LABELS.map((d, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setWeekday(i)}
                  className={`text-[11px] font-bold rounded-lg py-2 border transition ${weekday === i ? "bg-purple-600 border-purple-600 text-white" : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300"}`}
                  data-testid={`companion-shot-reminder-day-${i}`}
                >
                  {d}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[11px]">Time</Label>
              <Input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                className="mt-1 rounded-xl text-sm"
                data-testid="companion-shot-reminder-time-input"
              />
            </div>
            <div>
              <Label className="text-[11px]">Channel</Label>
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value)}
                className="mt-1 w-full rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm px-3 py-2"
                data-testid="companion-shot-reminder-channel-select"
              >
                <option value="email">Email</option>
                <option value="sms">SMS</option>
                <option value="both">Email + SMS</option>
              </select>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={create}
              disabled={saving}
              className="flex-1 rounded-xl bg-purple-600 hover:bg-purple-700 text-white text-sm"
              data-testid="companion-shot-reminder-save-btn"
            >
              {saving ? "Saving…" : "Save reminder"}
            </Button>
            <Button
              variant="outline"
              onClick={() => { setShowForm(false); setLabel(""); }}
              className="rounded-xl text-sm"
              data-testid="companion-shot-reminder-cancel-btn"
            >
              Cancel
            </Button>
          </div>
          <p className="text-[10px] text-slate-500">
            SMS uses the phone on file. Standard message rates may apply.
          </p>
        </div>
      )}
    </div>
  );
}
